import { FC, useEffect, useMemo, useState } from 'react';
import Picker from 'src/view/components/Controls/Pickers/Picker/Picker';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import { localeStore } from '~/mobx/localesStore';
import client from '~/services/client';
import logger from '~/services/logger';

interface IProps {
  timezone: string;
  onSelect: (value: string) => void;
}

interface ITimezone {
  id: number;
  name: string;
  sort: number;
}

export const TimeZonePicker: FC<IProps> = ({ timezone, onSelect }): JSX.Element => {
  const timezones = useTimezones();

  const timezonesOptions = useMemo(() => timezones.sort((a, b) => a.sort - b.sort).map((tz) => ({ value: tz.id, label: tz.name })), [timezones]);
  const selectedTimezoneOption: IPickerOption | undefined = useMemo(() => timezonesOptions?.find((tz) => tz.value === +timezone), [timezone]);

  const handleTimezoneSelect = (options: IPickerOption[]) => {
    const option = options[0];
    if (option) onSelect(option.value);
  };

  return (
    <Picker
      options={timezonesOptions}
      selectedOptions={selectedTimezoneOption ? [selectedTimezoneOption] : undefined}
      onSelect={handleTimezoneSelect}
      numberVisibleOptionsInDropdown={5}
      placeholder={localeStore.t('select_time_zone')}
      hideResetIcon
      className="w-100"
      listClassName="w-100"
    />
  );
};

const useTimezones = () => {
  const [timezones, setTimezones] = useState<ITimezone[]>([]);

  useEffect(() => {
    const fetchTimezones = async () => {
      try {
        const timezones = await client.get('v3/timezones');
        setTimezones(timezones);
      } catch (error: any) {
        logger.error({ error, place: 'TimeZonePicker fetchTimezones' });
      }
    };

    fetchTimezones();
  }, []);

  return timezones;
};
